import { Fragment, useState } from 'react';
import moment from 'moment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { Alert, Box, Button, FormControlLabel, LinearProgress, Stack, Switch, TextField, Typography } from '../../ui/core';
import api from '../../lib/api';
import useSdpFetch from './useSdpFetch';
import useQueryState, { flag, isoDate, oneOf, serializeFlag, useSetQueryParams } from './useQueryState';
import StateGrid, { Legend, MAX_GRID_DAYS, gridCities, gridDays, unclaimedShare } from './StateGrid';
import { EnumPill, Muted, ShadowChip } from './parts';
import { GUARD_STATE_PALETTES, LABEL_PALETTES, MODE_PALETTES, formatNum, formatShare, formatTs } from './sdpFormatters';

export const FilterBar = ({ children }) => (
  <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', mb: '12px' }}>{children}</Box>
);

export const DateField = ({ name, label, value, onChange }) => (
  <TextField
    name={name}
    label={label}
    type="date"
    size="small"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    InputLabelProps={{ shrink: true }}
  />
);

const DecisionDetail = ({ decision }) => {
  if (!decision) {
    return <Muted>No decision row (mode off: state only, SDP decides nothing here).</Muted>;
  }
  const items = [
    `ts ${formatTs(decision.ts)}`,
    `trigger ${decision.trigger}`,
    `readBucket ${decision.readBucket || '—'}`,
    `label ${decision.label}`,
    `need ${formatNum(decision.need)}`,
    `urgency ${formatNum(decision.urgency)}`,
    `target ${formatNum(decision.targetMult)} → current ${formatNum(decision.currentMult)}`,
    `stepClamped ${decision.stepClamped ? 'yes' : 'no'}`,
    `armApplied ${formatNum(decision.armApplied)}`,
    `guardState ${decision.guardState}`,
  ];
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
      {items.map((item) => (
        <Typography key={item} sx={{ fontSize: '12.5px' }}>
          {item}
        </Typography>
      ))}
    </Box>
  );
};

// `paid` is history from sdp_priced_trip, not today's mode: false = only
// dry-run pay was logged (log_only), so the multipliers are shadows; null =
// SDP priced nothing that day.
const MultCell = ({ row }) => {
  const shadow = row.paid === false;
  return (
    <TableCell data-testid="mult-cell">
      <Box component="span" data-shadow={shadow} sx={shadow ? { fontStyle: 'italic', color: 'text.secondary' } : undefined}>
        {`${formatNum(row.targetMult)} → ${formatNum(row.currentMult)}`}
      </Box>
      {shadow && <ShadowChip mode="log_only" />}
      {row.paid == null && <Muted>not priced</Muted>}
    </TableCell>
  );
};

// A city in the URL is trusted until Status has loaded the list, then checked
// against it.
export const knownCity = (cities) => (raw) => (cities.length === 0 || cities.some((c) => c.cityId === raw) ? raw : undefined);

const StateTab = ({ cities = [], today = moment().format('YYYY-MM-DD') }) => {
  const [cityId, setCityId] = useQueryState('cityId', { parse: knownCity(cities) });
  const [from, setFrom] = useQueryState('from', { parse: isoDate });
  const [to, setTo] = useQueryState('to', { parse: isoDate });
  const [view, setView] = useQueryState('view', { defaultValue: 'table', parse: oneOf(['table', 'grid']) });
  const [includeLogOnly, setIncludeLogOnly] = useQueryState('logOnly', {
    defaultValue: false,
    parse: flag,
    serialize: serializeFlag,
  });
  const setParams = useSetQueryParams();
  const [expanded, setExpanded] = useState({});

  const query = { cityId: cityId || undefined, from: from || undefined, to: to || undefined };
  const { data, loading, error } = useSdpFetch(() => api.supplyDemandPace.getState(query), JSON.stringify(query));
  const rows = (data && data.rows) || [];

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  // A grid cell drills into the table for that city-day; pushed, so back
  // returns to the grid.
  const openCityDay = (row) =>
    setParams({ view: null, cityId: row.cityId, from: row.departureDate, to: row.departureDate }, { push: true });
  const grid = view === 'grid';
  const { days, truncated } =
    grid && data ? gridDays({ from, to, dataFrom: data.from, dataTo: data.to, today }) : { days: [], truncated: false };

  return (
    <Stack spacing="8px">
      <FilterBar>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={view}
          onChange={(_e, value) => value && setView(value)}
          aria-label="State view"
        >
          <ToggleButton value="table">Table</ToggleButton>
          <ToggleButton value="grid">Grid</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          select
          name="cityId"
          label="City"
          size="small"
          value={cityId}
          onChange={(e) => setCityId(e.target.value)}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        >
          <option value="">All cities</option>
          {cities.map((c) => (
            <option key={c.cityId} value={c.cityId}>
              {c.cityName || c.cityId}
            </option>
          ))}
        </TextField>
        <DateField name="from" label="From (departure day)" value={from} onChange={setFrom} />
        <DateField name="to" label="To" value={to} onChange={setTo} />
        {grid && !cityId && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                name="logOnly"
                checked={includeLogOnly}
                onChange={(e) => setIncludeLogOnly(e.target.checked)}
              />
            }
            label={<Typography sx={{ fontSize: '13px' }}>Include log_only cities</Typography>}
          />
        )}
        {data && <Muted>{`Showing ${data.from} → ${data.to}`}</Muted>}
      </FilterBar>
      {grid ? (
        <>
          <Muted>
            Current multiplier per city and departure day → the target it is stepping toward, coloured by the decision
            label; a safe or frozen guard wins the colour. Hatched italic cells are shadow (log_only) days: computed and logged, never paid. Click a cell for its
            row in the table.
          </Muted>
          <Legend />
        </>
      ) : (
        <Muted>
          Rows marked shadow had only log_only (dry-run) pricing: multipliers were computed and logged but never paid.
          This is what was paid that day, not the city&apos;s mode today. &quot;not priced&quot; means SDP priced nothing
          that day.
        </Muted>
      )}
      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {grid && truncated && (
        <Alert severity="info">{`The grid shows ${MAX_GRID_DAYS} days at most: ${days[0]} → ${
          days[days.length - 1]
        }. Narrow From / To to see the rest, or use the table.`}</Alert>
      )}
      {grid ? (
        data && (
          <StateGrid
            rows={rows}
            cities={gridCities({ cities, rows, includeLogOnly, cityId })}
            days={days}
            today={today}
            onSelect={openCityDay}
          />
        )
      ) : !loading && !error && rows.length === 0 ? (
        <Muted>No city-day state for this filter.</Muted>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>City</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Unclaimed / board (unclaimed share)</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Arm</TableCell>
                <TableCell>Target → current</TableCell>
                <TableCell>Guard state</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const key = `${row.cityId}-${row.departureDate}`;
                return (
                  <Fragment key={key}>
                    <TableRow data-testid={`state-row-${key}`}>
                      <TableCell>{row.cityName || row.cityId}</TableCell>
                      <TableCell>{row.departureDate}</TableCell>
                      <TableCell>{`${row.unclaimed} / ${row.board} (${formatShare(unclaimedShare(row))})`}</TableCell>
                      <TableCell>
                        <EnumPill value={row.label} palettes={LABEL_PALETTES} />
                      </TableCell>
                      <TableCell>{row.ditherArm ?? '—'}</TableCell>
                      <MultCell row={row} />
                      <TableCell>
                        <EnumPill value={row.guardState} palettes={GUARD_STATE_PALETTES} />
                        {row.guardReason && (
                          <Typography component="span" sx={{ fontSize: '12px', ml: '6px' }}>
                            {row.guardReason}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatTs(row.updatedAt)}</TableCell>
                      <TableCell>
                        <EnumPill value={row.mode} palettes={MODE_PALETTES} />
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="text" onClick={() => toggle(key)}>
                          Decision
                        </Button>
                      </TableCell>
                    </TableRow>
                    {expanded[key] && (
                      <TableRow>
                        <TableCell colSpan={10}>
                          <DecisionDetail decision={row.latestDecision} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
};

export default StateTab;
